import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 60000 });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pbp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Statuses that indicate the platform/proxy failed, not our application logic.
const GATEWAY_STATUSES = new Set([500, 502, 503, 504, 520, 521, 522, 523, 524]);

const BUSY_MESSAGE = "Our servers are a little busy right now. Please try again in a few moments.";
const OFFLINE_MESSAGE = "We couldn't reach our servers. Please check your connection and try again.";
const GENERIC_MESSAGE = "Something went wrong. Please try again.";

function looksLikeHtml(value) {
  return typeof value === "string" && /<html|<!doctype|cloudflare|origin web server|gateway/i.test(value);
}

// Normalize every error into a clean, candidate-friendly message so raw
// proxy/HTML errors (e.g. Cloudflare 520) are never shown to end users.
// A usable JSON `detail` from our backend is preserved as-is.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const resp = error.response;

    if (!resp) {
      const friendly = error.code === "ECONNABORTED" ? BUSY_MESSAGE : OFFLINE_MESSAGE;
      error.friendlyMessage = friendly;
      error.response = { status: 0, data: { detail: friendly } };
      return Promise.reject(error);
    }

    const data = resp.data;
    const detail = data && typeof data === "object" ? data.detail : undefined;
    const hasUsableDetail = detail !== undefined && detail !== null && !looksLikeHtml(detail);

    if (!hasUsableDetail && (GATEWAY_STATUSES.has(resp.status) || looksLikeHtml(data) || typeof data === "string")) {
      const friendly = resp.status >= 500 || resp.status === 0 ? BUSY_MESSAGE : GENERIC_MESSAGE;
      error.friendlyMessage = friendly;
      resp.data = data && typeof data === "object" ? { ...data, detail: friendly } : { detail: friendly };
    }

    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return GENERIC_MESSAGE;
  if (typeof detail === "string") return looksLikeHtml(detail) ? BUSY_MESSAGE : detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : GENERIC_MESSAGE)).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return GENERIC_MESSAGE;
}

export default api;
